import React, { useState, useEffect, useRef } from 'react';
import { Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { DataProviderInstance } from '../../api/DataProvider';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_LIMITS } from '../../consts/SubscriptionFeatures';
import FeatureLockBanner from '../../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../../components/subscription/PlanUpgradeModal';
import useCurrentPlanLabel from '../../components/subscription/useCurrentPlanLabel';
import { limitActionHint, planLimitText } from '../../components/subscription/planLockTexts';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import Toast from '../../components/support/Toast';
import ResourceListCard from './components/ResourceListCard';
import ResourceFormPopup from './components/ResourceFormPopup';
import { cleanResourceSchedule } from '../../utils/resourceSchedule';
import { makeResourceSlug, previewResourceSlug, signatureUrl } from './components/resourceFormat';
import styles from './ResourceList.module.css';

const ITEMS_PER_PAGE = 10;

// Assinatura: só imagem (o servidor aceita os mesmos formatos)
const SIGNATURE_TYPES = ['image/png', 'image/jpeg'];
const SIGNATURE_EXTENSIONS = /\.(png|jpe?g)$/i;

const EMPTY_FORM = {
    label: '',
    regulator: '',
    expertise: '',
    phoneCountryCode: '55',
    phoneNumber: '',
    email: '',
    public_schedule_enabled: false,
    public_schedule_name: '',
    public_schedule_bio: '',
    public_schedule_slug: '',
    default_room_id: '',
    // Horário e limites próprios; null = segue a clínica
    schedule_config: null
};

/**
 * Agendas da equipe (/company/agendas): orquestra lista, formulário, inativar e reativar.
 * O limite do plano barra criar e reativar antes da API, com o aviso de troca de plano.
 */
const ResourceList = () => {
    const { GetCompanyResources, SetCompanyResource, UpdateCompanyResource, DeleteCompanyResource, CompanyConfig } = DataProviderInstance();
    const { hasRole } = useRoleAccess();
    const { getLimit } = useSubscriptionFeatures();
    // Aviso de limite do plano: false | 'create' | 'reactivate' (muda o que fazer no texto)
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    // Muda a cada recarga: a assinatura trocada aparece sem cache do navegador
    const [signatureVersion, setSignatureVersion] = useState(() => Date.now());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedResource, setSelectedResource] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState(null);
    const [showConfirmReactivate, setShowConfirmReactivate] = useState(false);
    const [resourceToReactivate, setResourceToReactivate] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Só proprietário e administradores criam, editam, inativam e reativam
    const canManageResources = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [labelError, setLabelError] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFilePreview, setSelectedFilePreview] = useState(null);
    const [signatureFilePath, setSignatureFilePath] = useState('');
    const inputFileRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadResources();
    }, []);

    const loadResources = () => {
        GetCompanyResources((result) => {
            setResources(result || []);
            setSignatureVersion(Date.now());
            setLoading(false);
        });
    };

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const activeResourcesCount = resources.filter(r => r.Active == 1).length;

    // Limite do plano (usado no botão de criar e no de reativar)
    const resourceLimit = getLimit(SUBSCRIPTION_LIMITS.RESOURCES);
    const isAtResourceLimit = resourceLimit !== null && activeResourcesCount >= resourceLimit;
    // Mesma frase do banner e do modal: limite, plano e o que fazer
    const planLabel = useCurrentPlanLabel();
    // Acima do limite (agendas de antes de um plano menor ou do fim do teste): diz
    // quantas estão ativas, senão "permite até 2" ao lado de 3 ativas parece erro
    const overLimitNote = isAtResourceLimit && activeResourcesCount > resourceLimit
        ? `Hoje ${activeResourcesCount} estão ativas. `
        : '';
    const createLimitHint = `${overLimitNote}${limitActionHint(true)}`;
    const resourceLimitMessage = isAtResourceLimit
        ? `${planLimitText(planLabel, resourceLimit, 'agenda', 'agendas')}. ${createLimitHint}`
        : '';
    // Dica do botão de reativar no limite: mesma frase do aviso
    const reactivateLimitMessage = isAtResourceLimit
        ? `${planLimitText(planLabel, resourceLimit, 'agenda ativa', 'agendas ativas')}. ${limitActionHint(true, true)}`
        : '';
    const addBlockedReason = !canManageResources
        ? 'Apenas proprietários e administradores podem adicionar agendas'
        : resourceLimitMessage;

    const getSignatureSrc = (resource) => signatureUrl(CompanyConfig?.Id, resource, signatureVersion);

    // Salas ativas da empresa: escolha da sala padrão no formulário e selo na lista
    const rooms = CompanyConfig?.Rooms || [];
    const roomLabelById = (roomId) => rooms.find((room) => String(room.Id) === String(roomId))?.Label || '';

    // Link previsto pelo nome digitado: agenda nova, sem link ainda ou nome trocado (o backend refaz o slug ao salvar)
    const labelChanged = modalMode === 'create'
        || !selectedResource?.public_schedule_slug
        || makeResourceSlug(formData.label) !== makeResourceSlug(selectedResource?.Label);
    const previewSlug = labelChanged
        ? previewResourceSlug(CompanyConfig?.public_schedule_slug, CompanyConfig?.Id, formData.label)
        : '';

    const handleOpenModal = (mode, resource = null) => {
        // Verificar limite do plano para novos recursos
        if (mode === 'create' && isAtResourceLimit) {
            setUpgradeModalOpen('create');
            return;
        }

        setModalMode(mode);
        setSelectedResource(resource);
        // Telefone salvo: usa o countryCode quando existe; senão separa o DDI pelos dígitos
        let phoneCountryCode = String(resource?.countryCode || '55');
        let phoneNumber = '';
        if (resource?.Phone) {
            const digits = resource.Phone.replace(/\D/g, '');
            if (digits.length > 11) {
                phoneCountryCode = digits.substring(0, digits.length - 11);
                phoneNumber = digits.substring(digits.length - 11);
            } else {
                phoneNumber = digits;
            }
        }

        setFormData(resource ? {
            label: resource.Label,
            regulator: resource.Regulator,
            expertise: resource.Expertise || '',
            phoneCountryCode,
            phoneNumber,
            email: resource.Email || '',
            public_schedule_enabled: !!resource.public_schedule_enabled,
            public_schedule_name: resource.public_schedule_name || '',
            public_schedule_bio: resource.public_schedule_bio || '',
            public_schedule_slug: resource.public_schedule_slug || '',
            default_room_id: resource.DefaultRoomId || '',
            schedule_config: resource.Schedule || null
        } : EMPTY_FORM);

        setLabelError('');
        setSelectedFile(null);
        setSignatureFilePath(resource?.Signature || '');
        setIsModalOpen(true);
    };

    const clearFile = () => {
        if (inputFileRef.current) {
            inputFileRef.current.value = '';
        }
        setSelectedFile(null);
        // Libera a URL da prévia
        if (selectedFilePreview) {
            URL.revokeObjectURL(selectedFilePreview);
            setSelectedFilePreview(null);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedResource(null);
        setFormData(EMPTY_FORM);
        setSelectedFile(null);
        setSelectedFilePreview(null);
        setSignatureFilePath('');
        clearFile();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        // "Todos os arquivos" no seletor deixa escolher qualquer coisa: recusa na hora
        if (file && !SIGNATURE_TYPES.includes(file.type) && !SIGNATURE_EXTENSIONS.test(file.name)) {
            event.target.value = '';
            showToast('Escolha uma imagem PNG ou JPG', 'error');
            return;
        }
        setSelectedFile(file);
        if (file) {
            if (selectedFilePreview) {
                URL.revokeObjectURL(selectedFilePreview);
            }
            setSelectedFilePreview(URL.createObjectURL(file));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'label' && labelError) setLabelError('');
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const setFieldValue = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        // Nome obrigatório ao criar e ao editar
        if (!String(formData.label || '').trim()) {
            setLabelError('Informe o nome da agenda');
            return;
        }

        // Telefone (só os dígitos locais) e DDI vão separados
        const phoneDigits = formData.phoneNumber.replace(/\D/g, '');

        const data = {
            ...formData,
            phone: phoneDigits,
            countryCode: String(formData.phoneCountryCode || '55').replace(/\D/g, ''),
            signatureFile: selectedFile,
            signature: signatureFilePath,
            public_schedule_enabled: formData.public_schedule_enabled ? 1 : 0,
            public_schedule_name: formData.public_schedule_name,
            public_schedule_bio: formData.public_schedule_bio,
            public_schedule_slug: formData.public_schedule_slug,
            schedule_config: cleanResourceSchedule(formData.schedule_config)
        };

        if (modalMode == 'create') {
            SetCompanyResource(data, (result) => {
                if (result?.code === 'BLOCKED_ON_PLAN') {
                    // O servidor também barra no limite (ex.: outra aba criou antes)
                    handleCloseModal();
                    loadResources();
                    setUpgradeModalOpen('create');
                } else if (result.error) {
                    showToast(result.error || 'Erro ao criar a agenda. Tente novamente.', 'error');
                } else {
                    showToast('Agenda criada', 'success');
                    loadResources();
                    handleCloseModal();
                }
            });
        } else {
            UpdateCompanyResource({
                ...data,
                id: selectedResource.Id
            }, (result) => {
                if (result.error) {
                    showToast(result.error || 'Erro ao atualizar a agenda. Tente novamente.', 'error');
                } else {
                    showToast('Agenda atualizada', 'success');
                    loadResources();
                    handleCloseModal();
                }
            });
        }
    };

    const handleRemoveSignature = () => {
        const data = {
            ...formData,
            // Só tira a assinatura: o horário em edição vai no "Salvar"
            schedule_config: undefined,
            removeSignature: true,
            id: selectedResource.Id
        };

        UpdateCompanyResource(data, (result) => {
            if (result.error) {
                showToast(result.error || 'Erro ao remover assinatura. Tente novamente.', 'error');
            } else {
                showToast('Assinatura removida com sucesso!', 'success');
                loadResources();
                setSignatureFilePath('');
                // O popup continua aberto, já sem a assinatura
            }
        });
    };

    const handleOpenDeleteModal = (resource) => {
        setResourceToDelete(resource);
        setShowConfirmDelete(true);
    };

    const handleCloseDeleteModal = () => {
        setShowConfirmDelete(false);
        setResourceToDelete(null);
    };

    const handleDelete = () => {
        if (resourceToDelete) {
            DeleteCompanyResource(resourceToDelete.Id, (result) => {
                if (result.error) {
                    showToast(result.error || 'Erro ao inativar a agenda. Tente novamente.', 'error');
                } else {
                    showToast('Agenda inativada', 'success');
                    loadResources();
                    handleCloseDeleteModal();
                }
            });
        }
    };

    const handleOpenReactivateModal = (resource) => {
        // No limite do plano, reativar abre o aviso com o atalho de troca de plano
        if (isAtResourceLimit) {
            setUpgradeModalOpen('reactivate');
            return;
        }
        setResourceToReactivate(resource);
        setShowConfirmReactivate(true);
    };

    const handleCloseReactivateModal = () => {
        setShowConfirmReactivate(false);
        setResourceToReactivate(null);
    };

    const handleReactivate = () => {
        if (resourceToReactivate) {
            UpdateCompanyResource({ id: resourceToReactivate.Id, active: 1 }, (result) => {
                if (result?.code === 'BLOCKED_ON_PLAN') {
                    // O servidor também barra no limite (ex.: outra aba reativou antes)
                    handleCloseReactivateModal();
                    loadResources();
                    setUpgradeModalOpen('reactivate');
                } else if (result.error) {
                    showToast(result.error || 'Erro ao reativar a agenda. Tente novamente.', 'error');
                } else {
                    showToast('Agenda reativada', 'success');
                    loadResources();
                    handleCloseReactivateModal();
                }
            });
        }
    };

    const totalPages = Math.ceil(resources.length / ITEMS_PER_PAGE);
    const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentItems = resources.slice(indexOfFirstItem, indexOfFirstItem + ITEMS_PER_PAGE);

    return (
        <div className="page-content">
            <div className={styles.container}>
                <PlanUpgradeModal
                    isOpen={!!upgradeModalOpen}
                    onClose={() => setUpgradeModalOpen(false)}
                    featureName="agendas"
                    featureNameSingular="agenda"
                    type="limit"
                    limit={resourceLimit}
                    hint={limitActionHint(true, upgradeModalOpen === 'reactivate')}
                />

                <div className={styles.headerContainer}>
                    <AreaTitle
                        Title="Agendas"
                        Description="Uma agenda para cada profissional, com contato, assinatura e agendamento online."
                        Icon={<CalendarMonthOutlinedIcon />}
                    />
                    <Tooltip title={addBlockedReason} arrow>
                        <span>
                            <CustomButton
                                style="main"
                                variant="contained"
                                icon={<AddRoundedIcon />}
                                label="Nova agenda"
                                onClick={() => handleOpenModal('create')}
                                enabled={canManageResources}
                            />
                        </span>
                    </Tooltip>
                </div>

                {isAtResourceLimit && (
                    <FeatureLockBanner
                        type="limit"
                        lockIcon
                        featureName="agendas"
                        featureNameSingular="agenda"
                        currentCount={activeResourcesCount}
                        limit={resourceLimit}
                        hint={createLimitHint}
                        onUpgradeClick={() => setUpgradeModalOpen('create')}
                    />
                )}

                <ResourceListCard
                    loading={loading}
                    items={currentItems}
                    activeCount={activeResourcesCount}
                    totalCount={resources.length}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(event, value) => setCurrentPage(value)}
                    getSignatureSrc={getSignatureSrc}
                    getDefaultRoomLabel={(resource) => roomLabelById(resource.DefaultRoomId)}
                    canManage={canManageResources}
                    addBlockedReason={addBlockedReason}
                    reactivateHint={reactivateLimitMessage}
                    onAdd={() => handleOpenModal('create')}
                    onEdit={(resource) => handleOpenModal('edit', resource)}
                    onInactivate={handleOpenDeleteModal}
                    onReactivate={handleOpenReactivateModal}
                />
            </div>

            {isModalOpen && (
                <ResourceFormPopup
                    isCreate={modalMode == 'create'}
                    formData={formData}
                    labelError={labelError}
                    companyScheduleEnabled={!!Number(CompanyConfig?.public_schedule_enabled)}
                    companyConfig={CompanyConfig}
                    rooms={rooms}
                    onInputChange={handleInputChange}
                    onFieldValue={setFieldValue}
                    slug={selectedResource?.public_schedule_slug}
                    previewSlug={previewSlug}
                    signature={{
                        signatureFilePath,
                        currentSrc: getSignatureSrc(selectedResource),
                        selectedFile,
                        selectedFilePreview,
                        inputFileRef,
                        onFileChange: handleFileChange,
                        onClearFile: clearFile,
                        onRemoveSignature: handleRemoveSignature,
                    }}
                    onCopied={() => showToast('Link copiado', 'success')}
                    onSubmit={handleSubmit}
                    onClose={handleCloseModal}
                />
            )}

            <ConfirmPopup
                Visibility={showConfirmDelete}
                Title="Inativar agenda"
                Description="A agenda sai da tela de marcação, mas o histórico continua. Dá para reativar depois."
                ConfirmLabel="Inativar"
                OnConfirm={handleDelete}
                OnHide={handleCloseDeleteModal}
            />

            <ConfirmPopup
                Visibility={showConfirmReactivate}
                Title="Reativar agenda"
                Description={`Deseja reativar a agenda "${resourceToReactivate?.Label}"? Ela volta a aparecer na tela de marcação.`}
                ConfirmLabel="Reativar"
                OnConfirm={handleReactivate}
                OnHide={handleCloseReactivateModal}
            />

            <Toast
                open={toast.open}
                onClose={() => setToast({ ...toast, open: false })}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={6000}
            />
        </div>
    );
};

export default ResourceList;
